import React, { useState } from 'react';
import { evictionService } from '../../services/api';

interface MarkAsServedModalProps {
  caseData: any;
  onClose: () => void;
  onComplete: () => void;
}

export default function MarkAsServedModal({ caseData, onClose, onComplete }: MarkAsServedModalProps) {
  const [serviceDate, setServiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [serviceMethod, setServiceMethod] = useState('email');
  const [servingAgentName, setServingAgentName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [certificatePdf, setCertificatePdf] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!serviceDate || !serviceMethod || !servingAgentName.trim()) {
      setError('Please fill in all fields.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await evictionService.markAsServed(caseData.id, {
        serviceDate,
        serviceMethod,
        servingAgentName: servingAgentName.trim(),
      });

      if (res.data?.data?.certificatePdfBase64) {
        setCertificatePdf(res.data.data.certificatePdfBase64);
      } else {
        onComplete();
      }
    } catch (err: any) {
      console.error('Mark as served failed:', err);
      setError(err.response?.data?.message || 'Failed to record service. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadCertificate = () => {
    if (!certificatePdf) return;
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${certificatePdf}`;
    link.download = `Certificate_of_Service_${caseData.id}.pdf`;
    link.click();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <i className="fas fa-check-circle text-blue-600"></i>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Mark Notice as Served
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Record the service of this possession notice. A Certificate of Service will be generated and stored in the Evidence Safe.
                </p>

                {!certificatePdf ? (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Service Date *</label>
                      <input
                        type="date"
                        value={serviceDate}
                        onChange={(e) => setServiceDate(e.target.value)}
                        className="mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Service Method *</label>
                      <select
                        value={serviceMethod}
                        onChange={(e) => setServiceMethod(e.target.value)}
                        className="mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      >
                        <option value="email">Email</option>
                        <option value="post">First Class Post</option>
                        <option value="hand_delivery">Hand Delivery</option>
                        <option value="document_exchange">Document Exchange</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Serving Agent Name *</label>
                      <input
                        type="text"
                        value={servingAgentName}
                        onChange={(e) => setServingAgentName(e.target.value)}
                        placeholder="e.g. Jane Smith"
                        className="mt-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    {error && (
                      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                        {error}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <i className="fas fa-check-circle text-green-600 text-xl mr-2"></i>
                        <div>
                          <p className="text-sm font-medium text-green-900">Notice marked as served</p>
                          <p className="text-xs text-green-700 mt-1">
                            Certificate of Service generated and stored in Evidence Safe
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      This certificate includes SHA-256 hashing for court-admissible proof of service.
                      Download and save for your records.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {!certificatePdf ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {submitting ? 'Processing...' : 'Confirm Service'}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleDownloadCertificate}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <i className="fas fa-download mr-2"></i> Download Certificate
                </button>
                <button
                  type="button"
                  onClick={onComplete}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Done
                </button>
              </>
            )}
            {!certificatePdf && (
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
